-- | View-only work-queue state.
-- |
-- | This module contains non-routable, throwaway UI state that should not be
-- | treated as authoritative queue/domain state.
module Steps.WorkQueue.UIState
    ( ExpandedState(..)
    , WorkQueueUiState
    , initialWorkQueueUiState
    , resetWorkQueueUiState
    , expandNavigationTarget
    , preserveCurrentExpansion
    , setFormExpanded
    ) where

import Common

import API.Search (SearchSemester, SearchTeacher, semesterSearchAff, teacherSearchAff)
import API.Types (StudentID)
import Data.Map as Map
import Steps.WorkQueue.DocumentIndex (DocumentLookup)
import Widgets.BetterSelect (Select, SelectOption, defSelectConfig, initSelect', setSelected)
import Widgets.Search (SearchState, initSearchState)

data ExpandedState = Expanded | Collapsed

type WorkQueueUiState =
    { expandedStudents :: Map StudentID ExpandedState
    , expandedForms :: Map DocumentLookup ExpandedState
    , search :: SearchState
    , teacher :: Select SearchTeacher
    -- ^ Teacher select widget state. It mirrors `WorkQueueState.teacher`, which
    -- remains authoritative for document creation and saves.

    , semester :: Select SearchSemester
    -- ^ Semester select widget state. It mirrors `WorkQueueState.semester`,
    -- which remains authoritative for document creation and saves.
    }

initialWorkQueueUiState :: WorkQueueUiState
initialWorkQueueUiState =
    { expandedStudents: Map.empty
    , expandedForms: Map.empty
    , search: initSearchState
    , teacher: initSelect' "teacher" teacherSearchAff teacherSel
    , semester: initSelect' "semester" semesterSearchAff semesterSel
    }
  where
    teacherSel = defSelectConfig
        { placeholder = Just $ lookupTranslation "search-teacher"
        , allowDeselect = false
        }
    semesterSel = defSelectConfig
        { placeholder = Just $ lookupTranslation "search-semester"
        , allowDeselect = false
        }

resetWorkQueueUiState
    :: Maybe (SelectOption SearchTeacher)
    -> Maybe (SelectOption SearchSemester)
    -> WorkQueueUiState
    -> WorkQueueUiState
resetWorkQueueUiState defTeacher defSemester ui =
    ui
        { expandedStudents = Map.empty
        , expandedForms = Map.empty
        , teacher = setSelected ui.teacher defTeacher
        , semester = setSelected ui.semester defSemester
        }

-- | Expand the student and form that the user is navigating to.
-- |
-- | For example: user clicks a form in the workqueue and it should be
-- | expanded.
expandNavigationTarget :: DocumentLookup -> WorkQueueUiState -> WorkQueueUiState
expandNavigationTarget lookup ui =
    ui
        { expandedStudents = Map.insert lookup.studentId Expanded ui.expandedStudents
        , expandedForms = Map.insert lookup Expanded ui.expandedForms
        }

-- | Ensure `WorkQueueUiState` reflects the currently expanded student and form
-- | Useful after first load of Step 3, where a form and student are expanded,
-- | as they are the default selection, but that selection is not in
-- | `WorkQueueUiState`.
-- |
-- | Explicit user choices, including `Collapsed`, always win.
preserveCurrentExpansion
    :: Maybe DocumentLookup
    -> WorkQueueUiState
    -> WorkQueueUiState
preserveCurrentExpansion Nothing ui = ui
preserveCurrentExpansion (Just current) ui =
    ui
        { expandedStudents = preserve current.studentId ui.expandedStudents
        , expandedForms = preserve current ui.expandedForms
        }
  where
    -- | Inserts an `ExpandedState` record into `WorkQueueUiState` only if one
    -- | is not already present. This preserves implicitly chosen selections,
    -- | like the selected form being expanded on first load, and also
    -- | preserves the user's explicit choices of which students and forms are
    -- | expanded/collapsed.
    preserve :: forall key. Ord key => key -> Map key ExpandedState -> Map key ExpandedState
    preserve key states = case Map.lookup key states of
        Nothing -> Map.insert key Expanded states
        Just _ -> states

setFormExpanded :: DocumentLookup -> ExpandedState -> WorkQueueUiState -> WorkQueueUiState
setFormExpanded lookup expansion ui =
    ui { expandedForms = Map.insert lookup expansion ui.expandedForms }
